import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';

/* Urban Sports Club am Empfang (30.09.2026): USC-Gast in zwei Tipps erfassen —
   bekannten Gast wählen (Name/USC-ID) oder neu anlegen, Kurs von heute antippen.
   Der Besuch belegt den Kursplatz, zählt aber nicht in die Mitglieder-Check-ins. */
export default function UscGastDialog({ open, onClose, onErfasst }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [gast, setGast] = useState(null);
  const [neu, setNeu] = useState({ name: '', usc_id: '', telefon: '' });
  const [kurse, setKurse] = useState([]);
  const [kurs, setKurs] = useState(null);
  const [heute, setHeute] = useState([]);
  const [busy, setBusy] = useState(false);

  const ladeHeute = () => api.get('/usc/heute').then(r => setHeute(r.data.besuche || [])).catch(() => {});
  useEffect(() => {
    if (!open) return;
    setQ(''); setGast(null); setNeu({ name: '', usc_id: '', telefon: '' }); setKurs(null);
    api.get('/kursplan/heute').then(r => setKurse(r.data.kurse || [])).catch(() => setKurse([]));
    ladeHeute();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => {
      api.get('/usc/gaeste', { params: { q: q.trim() } }).then(r => setTreffer(r.data.gaeste || [])).catch(() => setTreffer([]));
    }, 200);
    return () => clearTimeout(h);
  }, [q, open]);

  const erfassen = async () => {
    if (!gast && neu.name.trim().length < 2) { toast.error('Namen eintragen oder einen bekannten Gast wählen'); return; }
    setBusy(true);
    try {
      const body = gast ? { lead_id: gast.id } : { name: neu.name.trim(), usc_id: neu.usc_id.trim(), telefon: neu.telefon.trim() };
      const r = (await api.post('/usc/besuch', { ...body, course_id: kurs?.id || null })).data;
      toast.success(`${r.name} erfasst${r.course_name ? ` · ${r.course_name}` : ''} — ${r.besuche_gesamt}. Besuch`);
      if (r.stammgast) toast.info(`${r.name} ist Stammgast — Mitgliedschaft anbieten (Aufgabe angelegt)`);
      setGast(null); setNeu({ name: '', usc_id: '', telefon: '' }); setKurs(null); setQ('');
      ladeHeute(); onErfasst?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht erfasst'); }
    setBusy(false);
  };
  const storno = async (b) => {
    try { await api.post(`/usc/besuch/${b.id}/storno`); toast.success('Besuch storniert'); ladeHeute(); onErfasst?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht storniert'); }
  };

  const chip = (aktiv) => `rounded-full border px-3 py-1.5 text-sm ${aktiv ? 'border-marke bg-marke/10 font-semibold' : 'border-border hover:bg-muted'}`;
  const offen = kurse.filter(k => !k.vorbei);
  const vorbei = kurse.filter(k => k.vorbei);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="usc-dialog">
        <DialogHeader><DialogTitle>Urban Sports Club · Gast erfassen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Wer ist da?</p>
            {gast ? (
              <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                <span><b>{gast.name}</b> <span className="text-xs text-muted-foreground">{gast.usc_id ? `USC ${gast.usc_id} · ` : ''}{gast.usc_besuche || 0} Besuche bisher</span></span>
                <button type="button" className="text-xs underline text-muted-foreground" onClick={() => setGast(null)}>ändern</button>
              </div>
            ) : (
              <>
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Bekannter Gast: Name oder USC-ID suchen" className="h-10" data-testid="usc-suche" />
                {treffer.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {treffer.map(t => (
                      <button key={t.id} type="button" className={chip(false)} onClick={() => setGast(t)}>
                        {t.name} <span className="text-xs text-muted-foreground">· {t.usc_besuche || 0}×</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground pt-1">Neu hier? Dann kurz eintragen:</p>
                <div className="grid sm:grid-cols-3 gap-2">
                  <Input value={neu.name} onChange={e => setNeu({ ...neu, name: e.target.value })} placeholder="Vor- und Nachname" className="h-10 sm:col-span-3" data-testid="usc-name" />
                  <Input value={neu.usc_id} onChange={e => setNeu({ ...neu, usc_id: e.target.value })} placeholder="USC-ID (optional)" className="h-10" />
                  <Input value={neu.telefon} onChange={e => setNeu({ ...neu, telefon: e.target.value })} placeholder="Telefon (optional)" inputMode="tel" className="h-10 sm:col-span-2" />
                </div>
              </>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Welcher Kurs? <span className="normal-case font-normal">(belegt einen Platz)</span></p>
            <div className="flex flex-wrap gap-1.5">
              {offen.map(k => (
                <button key={k.id} type="button" className={chip(kurs?.id === k.id)} onClick={() => setKurs(kurs?.id === k.id ? null : k)}>
                  {k.start} {k.name} <span className="text-xs text-muted-foreground">{k.gebucht}/{k.max}</span>
                </button>
              ))}
              <button type="button" className={chip(!kurs)} onClick={() => setKurs(null)}>Ohne Kurs (Gym)</button>
            </div>
            {vorbei.length > 0 && (
              <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">Kurse von vorhin nachtragen</summary>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {vorbei.map(k => <button key={k.id} type="button" className={chip(kurs?.id === k.id)} onClick={() => setKurs(k)}>{k.start} {k.name}</button>)}
                </div>
              </details>
            )}
          </div>

          <Button className="w-full h-11 text-base bg-marke hover:bg-marke-dunkel" disabled={busy} onClick={erfassen} data-testid="usc-erfassen">
            Besuch erfassen
          </Button>

          {heute.length > 0 && (
            <div className="border-t border-border pt-3 space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Heute erfasst ({heute.length})</p>
              {heute.map(b => (
                <div key={b.id} className="flex items-center justify-between text-sm">
                  <span>{b.name} <span className="text-xs text-muted-foreground">{b.course_name ? `· ${b.kurs_start} ${b.course_name}` : '· Gym'}</span></span>
                  <button type="button" className="text-xs text-muted-foreground underline" onClick={() => storno(b)}>stornieren</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
